import { useCommonTracking } from 'common-tracking';
import {
  Button,
  css,
  Spacer,
  styled,
  Text,
  theme,
  useToaster,
} from 'design-system';
import { useEffect, useState } from 'react';

import { useAuthContext } from '../auth-context';
import { AUTH_TOKEN_KEY } from '../constants';
import { useGoogleSignIn } from '../google-signin';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Office: any;

export const SocialLoginDialog = ({
  onSignIn,
  checkFirstTimeUse,
}: {
  onSignIn?: () => void;
  checkFirstTimeUse?: (token: string) => void;
}) => {
  const {
    authState,
    editage,
    listeners: { onUserCreated },
  } = useAuthContext();
  const { trackCommonEvent } = useCommonTracking();
  const { showToast } = useToaster();
  const [dialogUrl, setDialogUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const { googleLogIn } = useGoogleSignIn({
    baseUrl: editage.baseUrl,
    product: editage.product,
    useCustomRlifeSocialAuth: false,
    on: {
      loggedIn: async (token, ssoUserId, isExistingUser) => {
        if (checkFirstTimeUse) {
          await checkFirstTimeUse(token);
        }
        if (isExistingUser === 'true') {
          trackCommonEvent({
            type: 'login-in',
            userId: ssoUserId,
            authChannel: 'google',
          });
        } else {
          console.info(`Event called | sign-up | ${ssoUserId}`);
          trackCommonEvent({
            type: 'sign-up',
            userId: ssoUserId,
            success: true,
            authChannel: 'google',
          });
        }
        onUserCreated(token);
        await onSignIn?.();
        authState.invalidate();
        setLoading(false);
      },
      tokenObtained: (token, ssoUserId) => {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem('ssoUserId', ssoUserId.toString());
      },
      error: (errorVal) => {
        setLoading(false);
        showToast({ color: 'danger', title: errorVal });
      },
    },
  });

  let loginDialog: Office.Dialog;

  useEffect(() => {
    const host = window.location.host;
    const callbackURL = encodeURIComponent(
      `${editage.baseUrl}/login/google?application=${editage.product}&redirect=https://${host}/googleLoginStatus.html`,
    );
    setDialogUrl(`https://${host}/googleLogin.html?redirectUrl=${callbackURL}`);
  }, [editage]);

  const officeJsDialog = async () => {
    await Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 40, width: 30 },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result: any) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.info(`${result.error.code} ${result.error.message}`);
        } else {
          loginDialog = result.value;
          //@ts-ignore
          loginDialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            processLoginMessage,
          );
          loginDialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            processLoginDialogEvent,
          );
        }
      },
    );

    const processLoginMessage = (
      arg: { message: string; origin: string | undefined } | { error: number },
    ) => {
      const messageFromDialog = JSON.parse(
        (arg as { message: string }).message,
      );

      if (messageFromDialog.status === 'success') {
        setLoading(true);
        googleLogIn({ gAccessToken: messageFromDialog.url });
        loginDialog.close();
      } else {
        loginDialog.close();
      }
    };

    const processLoginDialogEvent = (
      args: { message: string; origin: string | undefined } | { error: number },
    ) => {
      processDialogEvent(args);
    };
  };

  const processDialogEvent = (
    arg: { message: string; origin: string | undefined } | { error: number },
  ) => {
    switch ((arg as { error: number }).error) {
      case 12002:
        showToast({
          color: 'danger',
          title:
            'The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid.',
        });
        break;
      case 12003:
        showToast({
          color: 'danger',
          title:
            'The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required.',
        });
        break;
      case 12006:
        // 12006 means that the user closed the dialog instead of waiting for it to close.
        // It is not known if the user completed the login or logout, so assume the user is
        // logged out and revert to the app's starting state. It does no harm for a user to
        // press the login button again even if the user is logged in.
        showToast({ color: 'danger', title: 'Unable to log in with Google.' });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <OutlineButton
        loading={
          loading ||
          authState.name === 'checking if logged in' ||
          authState.name === 'logging in'
        }
        onClick={officeJsDialog}
      >
        <img src="/assets/social-icons/icon-google.svg" /> Continue with Google
      </OutlineButton>
      <Spacer height={20} />
      <SectionSeparator>
        <Text className={textGray()}>or continue with email</Text>
        <Line />
      </SectionSeparator>
      <Spacer height={20} />
    </>
  );
};

const OutlineButton = styled(Button, {
  background: theme.colors.white,
  width: '100%',
  border: `1px solid ${theme.colors.gray8}`,
  color: theme.colors.gray11,
  fontWeight: 500,
  fontSize: 16,
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
});

const SectionSeparator = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
});

const Line = styled('div', {
  width: '100%',
  height: '1px',
  background: theme.colors.gray8,
  position: 'absolute',
  top: '13px',
  zIndex: -1,
});

const textGray = css({
  textAlign: 'center',
  padding: '0 13px',
  background: theme.colors.white,
  color: theme.colors.gray11,
});
